import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { UsersThree, ClockCounterClockwise, ChartLineUp, Envelope, Plus, Trash, TreeStructure, Sliders, Receipt as ReceiptIcon, PaperPlaneTilt, PaperPlaneRight, DeviceMobile, ClockClockwise, Eye, Bank, Shield } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid, LineChart, Line, Legend, AreaChart, Area } from "recharts";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { IssuanceSourceBadge } from "../components/IssuanceSourceBadge";
import { CardIssuanceDrawer } from "../components/CardIssuanceDrawer";
import { BulkMigrateDialog } from "../components/BulkMigrateDialog";
import { LogsTab } from "./admin/LogsTab";
import { AccountsReportTab } from "./admin/AccountsReportTab";
import { ImpersonationLogTab } from "./admin/ImpersonationLogTab";
import { RefundsTab } from "./admin/RefundsTab";
import { SupportTab } from "./admin/SupportTab";
import { InboxTab } from "./admin/InboxTab";
import { IssuanceLogTab } from "./admin/IssuanceLogTab";
import { ControlsTab } from "./admin/ControlsTab";
import { RatesTab } from "./admin/RatesTab";
import { HoldingBankTab } from "./admin/HoldingBankTab";
import { WireOutQueueTile } from "./admin/overview/WireOutQueueTile";
import { MoneyFlowAlertsTile } from "./admin/overview/MoneyFlowAlertsTile";
import { OutboundPayoutsTile } from "./admin/overview/OutboundPayoutsTile";
import { DigestPanel } from "./admin/overview/analytics/DigestPanel";
import { FunnelPanel } from "./admin/overview/analytics/FunnelPanel";
import { ScansPanel } from "./admin/overview/analytics/ScansPanel";
import { CardLayoutExperimentPanel } from "./admin/overview/analytics/CardLayoutExperimentPanel";
import { GeoPanel } from "./admin/overview/analytics/GeoPanel";
import { TransactionsPanel } from "./admin/overview/analytics/TransactionsPanel";
import { AccountingPanel } from "./admin/overview/analytics/AccountingPanel";
import { DeploymentHealthCard } from "./admin/overview/deployment/DeploymentHealthCard";
import LoginDigestCard from "./admin/overview/LoginDigestCard";
import { UsersTab } from "./admin/UsersTab";
import { DirectDepositsAdminTab } from "./admin/DirectDepositsAdminTab";
import { QuickAccessControlsCard } from "./admin/overview/QuickAccessControlsCard";
import { FraudTab } from "./admin/FraudTab";

const PALETTE = ["#184A30", "#D96C4A", "#4A7C59", "#CBA35C", "#8BA888"];

export default function AdminPanel() {
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [support, setSupport] = useState(null);
  const [accReport, setAccReport] = useState(null);
  const [admReport, setAdmReport] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [issuance, setIssuance] = useState([]);
  const [flags, setFlags] = useState(null);
  const [impLogs, setImpLogs] = useState([]);
  const [rates, setRates] = useState(null);
  const [holding, setHolding] = useState(null);

  const loadAll = async () => {
    const [u, l, r, s, a, m, t, i, f, il, rt, hb] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/login-logs"), api.get("/refunds"),
      api.get("/support/info"), api.get("/reports/accounts"), api.get("/reports/admin"),
      api.get("/support/tickets"), api.get("/admin/issuance-log"), api.get("/features"),
      api.get("/admin/impersonation-log"), api.get("/admin/rates"), api.get("/admin/holding-bank"),
    ]);
    setUsers(u.data); setLogs(l.data); setRefunds(r.data);
    setSupport(s.data); setAccReport(a.data); setAdmReport(m.data);
    setTickets(t.data); setIssuance(i.data); setFlags(f.data); setImpLogs(il.data);
    setRates(rt.data); setHolding(hb.data);
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <DeployMismatchBanner />
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Administration</p>
        <h1 className="serif text-5xl mt-2">Admin panel</h1>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-white border border-[#E5E0D8] rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview"><ChartLineUp size={16} className="mr-1" /> Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><UsersThree size={16} className="mr-1" /> Users</TabsTrigger>
            <TabsTrigger value="inbox" data-testid="tab-inbox"><Envelope size={16} className="mr-1" /> Inbox</TabsTrigger>
            <TabsTrigger value="issuance" data-testid="tab-issuance"><ReceiptIcon size={16} className="mr-1" /> Issuance log</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs"><ClockCounterClockwise size={16} className="mr-1" /> Login logs</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts"><UsersThree size={16} className="mr-1" /> Accounts</TabsTrigger>
            <TabsTrigger value="impersonation" data-testid="tab-impersonation"><Eye size={16} className="mr-1" /> View-as log</TabsTrigger>
            <TabsTrigger value="fraud" data-testid="tab-fraud"><Shield size={16} className="mr-1" /> Fraud</TabsTrigger>
            <TabsTrigger value="refunds" data-testid="tab-refunds">Refunds</TabsTrigger>
            <TabsTrigger value="controls" data-testid="tab-controls"><Sliders size={16} className="mr-1" /> Access & maintenance</TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates"><ReceiptIcon size={16} className="mr-1" /> Rates</TabsTrigger>
            <TabsTrigger value="holding" data-testid="tab-holding"><Bank size={16} className="mr-1" /> Holding bank</TabsTrigger>
            <TabsTrigger value="wires" data-testid="tab-wires"><Bank size={16} className="mr-1" /> Wire deposits</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support"><TreeStructure size={16} className="mr-1" /> Support info</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8" data-testid="panel-overview">
            {accReport && admReport && <Overview acc={accReport} adm={admReport} />}
          </TabsContent>

          <TabsContent value="users" className="mt-8" data-testid="panel-users">
            <UsersTab users={users} customers={users.filter(u => u.role === "customer")} reload={loadAll} />
          </TabsContent>

          <TabsContent value="inbox" className="mt-8" data-testid="panel-inbox">
            <InboxTab tickets={tickets} reload={loadAll} />
          </TabsContent>

          <TabsContent value="issuance" className="mt-8" data-testid="panel-issuance">
            <IssuanceLogTab rows={issuance} reload={loadAll} />
          </TabsContent>

          <TabsContent value="logs" className="mt-8" data-testid="panel-logs">
            <LogsTab logs={logs} />
          </TabsContent>

          <TabsContent value="accounts" className="mt-8" data-testid="panel-accounts">
            <AccountsReportTab />
          </TabsContent>

          <TabsContent value="impersonation" className="mt-8" data-testid="panel-impersonation">
            <ImpersonationLogTab rows={impLogs} />
          </TabsContent>

          <TabsContent value="fraud" className="mt-8" data-testid="panel-fraud">
            <FraudTab />
          </TabsContent>

          <TabsContent value="refunds" className="mt-8" data-testid="panel-refunds">
            <RefundsTab refunds={refunds} reload={loadAll} />
          </TabsContent>

          <TabsContent value="controls" className="mt-8" data-testid="panel-controls">
            {flags && <ControlsTab flags={flags} setFlags={setFlags} />}
          </TabsContent>

          <TabsContent value="rates" className="mt-8" data-testid="panel-rates">
            {rates && <RatesTab rates={rates} reload={loadAll} />}
          </TabsContent>

          <TabsContent value="holding" className="mt-8" data-testid="panel-holding">
            {holding && <HoldingBankTab holding={holding} reload={loadAll} />}
          </TabsContent>

          <TabsContent value="wires" className="mt-8" data-testid="panel-wires">
            <DirectDepositsAdminTab active={tab === "wires"} />
          </TabsContent>

          <TabsContent value="support" className="mt-8" data-testid="panel-support">
            {support && <SupportTab support={support} reload={loadAll} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Overview({ acc, adm }) {
  const roleData = Object.entries(adm.user_counts).map(([name, value]) => ({ name, value }));
  const [pendingApps, setPendingApps] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/admin/agent-applications?status=pending&limit=1");
        if (!cancelled) setPendingApps(r.data?.counts?.pending ?? 0);
      } catch (e) {
        if (!cancelled) setPendingApps(0);
      }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#184A30]/10 to-[#CBA35C]/10 border border-[#184A30]/20 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="setup-checklist-callout">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">New here? Or rolling out to production?</p>
          <p className="text-sm text-[#112318] mt-1">Step-by-step setup checklist with one-click deep-links to every integration provider.</p>
        </div>
        <Link to="/admin/setup"
          className="shrink-0 text-xs font-medium px-4 py-2 rounded-xl bg-[#184A30] hover:bg-[#0F3220] text-white"
          data-testid="setup-checklist-link">
          Open setup checklist →
        </Link>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="agents-admin-callout">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Agents & commissions</p>
          <p className="text-sm text-[#112318] mt-1">
            Onboard agents, view weekly/monthly/yearly commission totals, and mark payouts as paid.
            {pendingApps > 0 && (
              <span
                className="ml-2 inline-flex items-center gap-1 text-[#8C6B21] font-medium"
                data-testid="agents-admin-callout-pending-inline"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#CBA35C] opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#CBA35C]" />
                </span>
                {pendingApps} pending application{pendingApps === 1 ? "" : "s"} waiting for review.
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/agent-applications"
            className={`relative shrink-0 text-xs font-medium px-4 py-2 rounded-xl border ${pendingApps > 0 ? "bg-[#CBA35C]/15 border-[#CBA35C] text-[#8C6B21] hover:bg-[#CBA35C]/25" : "bg-white border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5"}`}
            data-testid="agent-applications-link">
            Review applications
            {pendingApps > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#CBA35C] text-white text-[10px] font-bold leading-none"
                data-testid="agent-applications-pending-badge"
              >
                {pendingApps > 99 ? "99+" : pendingApps}
              </span>
            )}
            <span className="ml-1">→</span>
          </Link>
          <Link to="/admin/agents"
            className="shrink-0 text-xs font-medium px-4 py-2 rounded-xl bg-[#184A30] hover:bg-[#0F3220] text-white"
            data-testid="agents-admin-link">
            Manage agents →
          </Link>
          <Link to="/admin/agent-payouts"
            className="shrink-0 text-xs font-medium px-4 py-2 rounded-xl bg-white border border-[#184A30] text-[#184A30] hover:bg-[#184A30]/5"
            data-testid="agent-payouts-link">
            Commission payouts →
          </Link>
        </div>
      </div>
      <QuickAccessControlsCard />
      <WireOutQueueTile />
      <MoneyFlowAlertsTile />
      <OutboundPayoutsTile />
      <DeploymentHealthCard />
      <LoginDigestCard />
      <EmailDeliveryBanner />
      <DigestPanel />
      <AccountingPanel />
      <TransactionsPanel />
      <FunnelPanel />
      <ScansPanel />
      <CardLayoutExperimentPanel />
      <ComplianceTile />
      <SharePanel />
      <GeoPanel />
      <div className="grid md:grid-cols-4 gap-6">
        <Kpi label="Total users" value={adm.total_users} />
        <Kpi label="Active cards" value={acc.total_cards_active} />
        <Kpi label="Principal (USD)" value={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(acc.total_principal_vnd)} />
        <Kpi label="Service fees (USD)" value={new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(acc.total_service_fee_vnd)} />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6">
          <h3 className="serif text-2xl mb-4">Cards by currency</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={acc.by_currency}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
              <XAxis dataKey="currency" stroke="#4A5C50" />
              <YAxis stroke="#4A5C50" />
              <Tooltip />
              <Bar dataKey="count" fill="#184A30" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6">
          <h3 className="serif text-2xl mb-4">User distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" outerRadius={100} label>
                {roleData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <Kpi label="Login attempts" value={adm.login_attempts} />
        <Kpi label="Failed logins" value={adm.failed_logins} />
        <Kpi label="Refund notices" value={adm.refund_notices} />
      </div>
    </div>
  );
}
const Kpi = ({ label, value }) => (
  <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 card-lift">
    <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">{label}</p>
    <p className="serif text-4xl mt-2 text-[#184A30]" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
  </div>
);



function DeployMismatchBanner() {
  // Production blast-radius scenario: admin is browsing on host A but the
  // backend has PUBLIC_BASE_URL=host B baked in env. Every email link, MMS
  // image URL, and password-reset link will route receivers to host B (the
  // wrong one), which fails for Twilio (image fetch) and confuses customers
  // (link to dev domain). This banner is intentionally LOUD — top of admin,
  // not dismissible, visible on every login until the env var is fixed.
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/config-health").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const base = (data.checks || []).find((c) => c.key === "base_url");
  if (!base) return null;

  const baseUrl = base.value || "";
  const browserHost = typeof window !== "undefined" ? window.location.host : "";
  let baseHost = "";
  try {
    baseHost = new URL(baseUrl).host;
  } catch {
    baseHost = baseUrl;
  }
  // Normalize www. prefix when comparing so www.zcardon.com == zcardon.com.
  const norm = (h) => (h || "").replace(/^www\./, "").toLowerCase();
  const mismatch = baseUrl && browserHost && norm(browserHost) !== norm(baseHost);

  // Three states we surface differently:
  //  1) HARD MISMATCH (admin browsing prod but PUBLIC_BASE_URL is preview)
  //     → rose banner — receivers will get broken links right now
  //  2) PREVIEW WARNING (admin browsing preview AND PUBLIC_BASE_URL is preview)
  //     → amber pre-deploy nudge
  //  3) IN-SYNC → no banner
  const isPreviewBase = /preview\.emergentagent\.com/i.test(baseUrl);
  const isProductionBrowser = !/preview\.emergentagent\.com|localhost/i.test(browserHost);

  if (mismatch && isProductionBrowser && isPreviewBase) {
    return (
      <div
        className="mb-6 rounded-2xl border-2 border-[#D96C4A] bg-[#D96C4A]/10 p-5"
        data-testid="deploy-mismatch-banner-hard"
      >
        <div className="flex items-start gap-3">
          <Shield size={28} weight="fill" className="text-[#B04E2F] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#B04E2F] font-bold">
              🔴 Critical · Deploy mismatch
            </p>
            <p className="serif text-2xl mt-1 text-[#B04E2F]">
              Production is embedding the WRONG host in every customer email and MMS.
            </p>
            <p className="text-sm text-[#112318] mt-2">
              You're administering this site from{" "}
              <span className="mono font-semibold">{browserHost}</span> but the backend has{" "}
              <span className="mono font-semibold">PUBLIC_BASE_URL = {baseUrl}</span> set.
              Every signed card-image URL, password-reset link, and email CTA your customers
              receive points at the dev/preview host — which causes Twilio MMS Error 11200/11205
              and breaks emailed links.
            </p>
            <ol className="text-sm text-[#112318] mt-3 space-y-1 list-decimal list-inside">
              <li>Open the <b>Emergent Deploy Panel → Environment Variables</b></li>
              <li>Set <span className="mono">PUBLIC_BASE_URL = https://{norm(browserHost)}</span></li>
              <li>Trigger a redeploy from the Deploy Panel</li>
              <li>Re-run <Link to="/admin/setup" className="underline">setup checklist smoke tests</Link> to verify</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  if (isPreviewBase && !isProductionBrowser) {
    return (
      <div
        className="mb-6 rounded-2xl border-2 border-[#CBA35C] bg-[#CBA35C]/10 p-4"
        data-testid="deploy-mismatch-banner-warn"
      >
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#8C6B21] font-bold">
          ⚠ Pre-deploy reminder
        </p>
        <p className="text-sm text-[#112318] mt-1">
          <span className="mono">PUBLIC_BASE_URL</span> is currently set to the preview host
          (<span className="mono">{baseHost}</span>). Before you flip production live, set this
          to your production domain (e.g.{" "}
          <span className="mono">https://www.zcardon.com</span>) in the Emergent Deploy Panel —
          otherwise customer-facing email/SMS links will point at the dev environment.
        </p>
      </div>
    );
  }

  return null;
}


function EmailDeliveryBanner() {
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/admin/email-config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  if (!cfg || dismissed) return null;
  // Only show the warning banner if we're on sandbox sender OR recent failures hit the free-tier wall.
  const needsAction = cfg.uses_sandbox_sender || cfg.recent_freetier_block;
  if (!needsAction && cfg.can_email_any_recipient) {
    return (
      <div className="bg-[#184A30]/5 border border-[#184A30]/20 rounded-2xl p-5 flex items-start gap-4" data-testid="email-delivery-ok">
        <span className="inline-flex w-10 h-10 rounded-full bg-[#184A30] text-white items-center justify-center shrink-0">
          <Envelope size={18} weight="duotone" />
        </span>
        <div className="flex-1 text-sm">
          <p className="serif text-xl text-[#112318]">Email delivery is live</p>
          <p className="text-[#4A5C50] mt-1">Sending from <span className="mono">{cfg.from}</span>. Emails can be delivered to any recipient.</p>
        </div>
        <button type="button" onClick={() => setDismissed(true)} className="text-xs text-[#4A5C50] hover:text-[#184A30]">Hide</button>
      </div>
    );
  }
  return (
    <div className="bg-[#D96C4A]/10 border border-[#D96C4A]/40 rounded-2xl p-5" data-testid="email-delivery-banner">
      <div className="flex items-start gap-4">
        <span className="inline-flex w-10 h-10 rounded-full bg-[#D96C4A] text-white items-center justify-center shrink-0">
          <Envelope size={18} weight="duotone" />
        </span>
        <div className="flex-1 text-sm">
          <p className="serif text-xl text-[#112318]">Emails are limited to your Resend account owner</p>
          <p className="text-[#4A5C50] mt-2 leading-relaxed">
            Zcardon is currently using Resend's shared sandbox sender
            (<span className="mono text-[#112318]">{cfg.from || "onboarding@resend.dev"}</span>).
            On the free tier this sender can only deliver to the email address you registered at resend.com.
            {cfg.recent_freetier_block && <> Recent issuance attempts were blocked with this exact error.</>}
          </p>
          <ol className="list-decimal ml-5 mt-3 space-y-1 text-[#4A5C50]">
            <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="link-ul text-[#184A30]">resend.com/domains</a> and add your domain.</li>
            <li>Add the DNS records Resend gives you (SPF, DKIM, DMARC) at your registrar.</li>
            <li>Once verified, set <span className="mono text-[#112318]">RESEND_FROM=Zcardon &lt;noreply@yourdomain.com&gt;</span> in <span className="mono">backend/.env</span> and restart the backend.</li>
          </ol>
          <p className="text-xs text-[#4A5C50] mt-3">
            Recent email attempts: <span className="mono">{cfg.recent_successes} delivered · {cfg.recent_failures} failed</span>
          </p>
        </div>
        <button type="button" onClick={() => setDismissed(true)} className="text-xs text-[#4A5C50] hover:text-[#184A30]" data-testid="email-banner-dismiss">Hide</button>
      </div>
    </div>
  );
}



function ComplianceTile() {
  // Surfaces OFAC + Issuing-reconciliation health on the admin dashboard.
  // Two-up grid: left card = OFAC pipeline health + open hits count,
  // right card = reconciliation drift status + latest run.
  const [ofac, setOfac] = useState(null);
  const [recon, setRecon] = useState(null);
  React.useEffect(() => {
    api.get("/admin/ofac/status").then((r) => setOfac(r.data)).catch(() => setOfac({ ok: false }));
    api.get("/admin/issuing-reconciliation/runs?limit=1").then((r) => setRecon(r.data)).catch(() => setRecon({ runs: [] }));
  }, []);
  const latestRun = recon?.runs?.[0];
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="admin-compliance-tile">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Compliance · BSA/AML</p>
          <p className="serif text-2xl mt-1">Sanctions screening + balance reconciliation</p>
        </div>
        <a
          href="/app/memory/COMPLIANCE_PROGRAM.md"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#184A30] underline mono"
        >
          View full BSA/AML program →
        </a>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        {/* OFAC summary */}
        <div className="rounded-xl border border-[#E5E0D8] p-4" data-testid="admin-compliance-ofac">
          <div className="flex items-center gap-2">
            <Shield size={18} weight="duotone" className="text-[#184A30]" />
            <p className="font-medium text-sm">OFAC SDN screening</p>
          </div>
          {ofac ? (
            <>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">SDN entities</p>
                  <p className="mono font-semibold text-[#184A30]">{(ofac.sdn_entries || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Open hits</p>
                  <p className={`mono font-semibold ${ofac.open_hits > 0 ? "text-[#B04E2F]" : "text-[#184A30]"}`}>
                    {ofac.open_hits || 0}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Threshold</p>
                  <p className="mono font-semibold text-[#184A30]">{ofac.match_threshold}%</p>
                </div>
              </div>
              {ofac.last_refreshed_at && (
                <p className="text-[10px] text-[#4A5C50] mono mt-2">
                  Last refresh: {new Date(ofac.last_refreshed_at).toLocaleString()}
                </p>
              )}
              <Link
                to="/admin/ofac-hits"
                className="inline-block text-xs text-[#184A30] underline mono mt-3"
                data-testid="admin-compliance-ofac-link"
              >
                Open review queue →
              </Link>
            </>
          ) : (
            <p className="text-xs text-[#4A5C50] mt-2">Loading…</p>
          )}
        </div>

        {/* Reconciliation summary */}
        <div className="rounded-xl border border-[#E5E0D8] p-4" data-testid="admin-compliance-recon">
          <div className="flex items-center gap-2">
            <ChartLineUp size={18} weight="duotone" className="text-[#184A30]" />
            <p className="font-medium text-sm">Issuing balance reconciliation</p>
          </div>
          {!latestRun && (
            <p className="text-xs text-[#4A5C50] mt-3">
              No reconciliation runs yet. Auto-starts once Stripe Issuing is funded
              and <span className="mono">ISSUANCE_PROVIDER=stripe</span>.
            </p>
          )}
          {latestRun && latestRun.ok && (
            <>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Stripe</p>
                  <p className="mono font-semibold text-[#184A30]">
                    ${(latestRun.stripe_balance_usd ?? 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Ledger</p>
                  <p className="mono font-semibold text-[#184A30]">
                    ${(latestRun.ledger_balance_usd ?? 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Drift</p>
                  <p className={`mono font-semibold ${latestRun.above_threshold ? "text-[#B04E2F]" : "text-[#184A30]"}`}>
                    {(latestRun.drift_usd ?? 0) >= 0 ? "+" : "−"}${Math.abs(latestRun.drift_usd || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-[#4A5C50] mono mt-2">
                Last run: {new Date(latestRun.checked_at).toLocaleString()}
              </p>
            </>
          )}
          {latestRun && !latestRun.ok && (
            <p className="text-xs text-[#4A5C50] mt-3 italic mono">
              {latestRun.skipped_reason}
            </p>
          )}
          <Link
            to="/admin/issuing-reconciliation"
            className="inline-block text-xs text-[#184A30] underline mono mt-3"
            data-testid="admin-compliance-recon-link"
          >
            Open drift history →
          </Link>
        </div>
      </div>
    </div>
  );
}

function SharePanel() {
  const [data, setData] = useState(null);
  React.useEffect(() => {
    api.get("/admin/share-analytics?since_days=30").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || data.total_shares_window === 0) return null;
  const CHANNEL_META = {
    email: { label: "Email receiver", color: "#184A30" },
    sms: { label: "Text (MMS)", color: "#D96C4A" },
    download: { label: "Download PNG", color: "#8BA888" },
    share_link: { label: "Copy share link", color: "#B04E2F" },
    whatsapp: { label: "WhatsApp", color: "#25D366" },
  };
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="share-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Share actions · last {data.window_days}d</p>
          <p className="serif text-2xl mt-1">
            {data.total_shares_window} <span className="text-[#4A5C50] text-lg">share{data.total_shares_window === 1 ? "" : "s"}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">All-time</p>
          <p className="serif text-2xl mt-1 mono" data-testid="share-lifetime">{data.total_shares_all_time}</p>
        </div>
      </div>
      <div className="mt-6 space-y-2">
        {data.channels.map((c) => {
          const meta = CHANNEL_META[c.channel] || { label: c.channel, color: "#4A5C50" };
          return (
            <div key={c.channel} className="flex items-center gap-3 text-sm" data-testid={`share-row-${c.channel}`}>
              <span className="w-32 truncate text-[#112318]">{meta.label}</span>
              <div className="flex-1 h-6 bg-[#FBF9F6] border border-[#E5E0D8] rounded overflow-hidden">
                <div
                  className="h-full text-[#FBF9F6] text-[11px] mono flex items-center justify-end pr-2"
                  style={{ width: `${Math.max(4, c.pct)}%`, background: meta.color }}
                >
                  {c.pct}%
                </div>
              </div>
              <span className="w-12 text-right mono text-[#4A5C50]">{c.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

